import { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  Phone,
  Lock,
  ArrowRight,
  ShieldCheck,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../assets/logo.png";


const API_URL = process.env.REACT_APP_BACKEND_URL;
if (!API_URL) {
  console.error("REACT_APP_BACKEND_URL is missing in frontend/.env");
}

function Login() {
  const navigate = useNavigate();

  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ mobile: "", password: "", form: "" });
  const [focused, setFocused] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem("rememberMobile");
    if (saved) {
      setMobile(saved);
      setRemember(true);
    }
  }, []);

  const validate = () => {
    const next = { mobile: "", password: "", form: "" };
    if (!/^[6-9]\d{9}$/.test(mobile)) next.mobile = "Enter a valid 10-digit mobile number";
    if (!password) next.password = "Password is required";
    else if (password.length < 4) next.password = "Password is too short";
    setErrors(next);
    return !next.mobile && !next.password;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      setErrors((p) => ({ ...p, form: "" }));
      const res = await axios.post(`${API_URL}/api/auth/login`, {
        mobile,
        password,
      });
      if (res.data.success) {

    localStorage.setItem("userId", res.data.userId);

    if (res.data.email) {
        localStorage.setItem("email", res.data.email);
    }

    if (remember) {
        localStorage.setItem("rememberMobile", mobile);
    } else {
        localStorage.removeItem("rememberMobile");
    }

    navigate("/otp", {
    state: {
        type: "login",
        mobile,
    },
});

}

else {
        setErrors((p) => ({ ...p, form: res.data.message || "Invalid credentials" }));
      }
    } catch (err) {
      setErrors((p) => ({
        ...p,
        form:
          err.response?.data?.detail ||
          err.response?.data?.message ||
          "Unable to connect. Please try again.",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] w-full bg-slate-50 relative overflow-hidden flex items-center justify-center px-5 py-10">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px);} to{opacity:1;transform:translateY(0);} }
        .anim-fade-up{animation:fadeUp .6s cubic-bezier(.22,1,.36,1) both;}
        .delay-1{animation-delay:.05s;} .delay-2{animation-delay:.15s;}
        .delay-3{animation-delay:.25s;} .delay-4{animation-delay:.35s;}
        .delay-5{animation-delay:.45s;} .delay-6{animation-delay:.55s;}
      `}</style>

      {/* Soft background accents */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full bg-sky-100/70 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Card */}
        <div className="anim-fade-up delay-2 rounded-2xl bg-white border border-slate-200/70 shadow-xl shadow-slate-200/60 p-7 sm:p-9">
          <div className="text-center mb-7">
            <div className="anim-fade-up delay-2 mx-auto mb-5 h-16 w-16 rounded-2xl overflow-hidden shadow-lg shadow-blue-500/25">
              <img src={logo} alt="ZA Optical" className="h-full w-full object-cover" />
            </div>
            <h1 className="anim-fade-up delay-3 text-[26px] sm:text-3xl font-semibold tracking-tight text-slate-900">
              Welcome back
            </h1>
            <p className="anim-fade-up delay-4 mt-2 text-sm text-slate-500">
              Sign in to manage your optical business.
            </p>
          </div>

          {errors.form && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{errors.form}</span>
            </div>
          )}

          <form onSubmit={handleLogin} noValidate className="space-y-5">
            {/* Mobile */}
            <div className="anim-fade-up delay-4">
              <label
                htmlFor="mobile"
                className="text-xs font-medium text-slate-700 mb-1.5 block"
              >
                Mobile Number
              </label>
              <div
                className={`flex items-center rounded-lg border bg-white transition
                  ${
                    errors.mobile
                      ? "border-red-400 ring-2 ring-red-100"
                      : focused === "mobile"
                      ? "border-blue-500 ring-4 ring-blue-100"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
              >
                <span className="pl-3.5 text-slate-400">
                  <Phone size={18} />
                </span>
                <span className="pl-2 pr-2 text-sm text-slate-500 border-r border-slate-200 py-3 select-none">
                  +91
                </span>
                <input
                  id="mobile"
                  data-testid="login-mobile-input"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  value={mobile}
                  onFocus={() => setFocused("mobile")}
                  onBlur={() => setFocused("")}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter your number"
                  className="w-full min-w-0 bg-transparent px-3 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none"
                  autoComplete="tel"
                  aria-invalid={!!errors.mobile}
                />
              </div>
              {errors.mobile && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.mobile}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="anim-fade-up delay-5">
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="password"
                  className="text-xs font-medium text-slate-700"
                >
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 transition"
                >
                  Forgot password?
                </Link>
              </div>
              <div
                className={`flex items-center rounded-lg border bg-white transition
                  ${
                    errors.password
                      ? "border-red-400 ring-2 ring-red-100"
                      : focused === "password"
                      ? "border-blue-500 ring-4 ring-blue-100"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
              >
                <span className="pl-3.5 text-slate-400">
                  <Lock size={18} />
                </span>
                <input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onFocus={() => setFocused("password")}
                  onBlur={() => setFocused("")}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full min-w-0 bg-transparent px-3 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none"
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="h-11 w-11 grid place-items-center text-slate-400 hover:text-slate-600 transition shrink-0"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.password}
                </p>
              )}
            </div>

            {/* Remember */}
            <label className="anim-fade-up delay-5 flex items-center gap-2.5 cursor-pointer select-none w-fit">
              <span className="relative inline-flex shrink-0">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={() => setRemember(!remember)}
                  className="peer sr-only"
                />
                <span className="h-5 w-9 rounded-full bg-slate-200 peer-checked:bg-blue-600 transition-colors" />
                <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
              </span>
              <span className="text-sm text-slate-600">Remember me</span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="anim-fade-up delay-6 group w-full rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 py-3.5 font-medium text-white shadow-md shadow-blue-500/20 transition-all
                        active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed min-h-[48px]
                        flex items-center justify-center gap-2 text-[15px]"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending OTP…
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight
                    size={18}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </>
              )}
            </button>

            {/* Trust bar */}
            <div className="flex items-center justify-center gap-1.5 pt-1 text-[11px] text-slate-500">
              <ShieldCheck size={13} className="text-emerald-500" />
              Secured with 256-bit encryption
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-400 tracking-wide">
          © 2026 ZA Optical
        </p>
      </div>
    </div>
  );
}

export default Login;